
import { Component, Vue } from 'vue-property-decorator';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

@Component
export default class PoseSimulator extends Vue {
    // --- UI状態 ---
    private isClosed: boolean = true;
    private isDancing: boolean = false;
    private isAudioEnabled: boolean = false;
    private lastPoseChangeTime: number = 0;
    private drawer: boolean = false; // 初期状態はメニューを閉じておく

    // --- Audio Nodes ---
    private audioCtx: AudioContext | null = null;
    private masterGain: GainNode | null = null;
    private masterFilter: BiquadFilterNode | null = null;
    private nextNoteTime: number = 0;
    private beatCount: number = 0;

    // --- 音階 (C Major Pentatonic) ---
    private scale: number[] = [
        130.81, 146.83, 164.81, 196.00, 220.00, // C3, D3, E3, G3, A3
        261.63, 293.66, 329.63, 392.00, 440.00, // C4, D4, E4, G4, A4
        523.25, 587.33, 659.25, 783.99, 880.00, // C5, D5, E5, G5, A5
        1046.50 // C6
    ];

    private axesHelper: THREE.AxesHelper | null = null;

    // --- スライダーの目標値 (v-modelと連動) ---
    private params: any = {
        tilt: 0, rArm: 0, lArm: 0, rLeg: 0, lLeg: 0,
        lean: 0, sideTilt: 0, rot: 0, zoom: 3, danceSpeed: 2.0,
        showGrid: true, // ガイド線の初期状態
        waveform: 'sine',
        frozen: {
            tilt: false, rArm: false, lArm: false, rLeg: false, lLeg: false,
            lean: false, sideTilt: false, rot: false, zoom: false
        }
    };

    // --- 描画用の現在値 (Lerpで目標値に近づく) ---
    private current: any = {
        tilt: 0, rArm: 0, lArm: 0, rLeg: 0, lLeg: 0,
        lean: 0, sideTilt: 0, rot: 0, zoom: 3
    };

    // --- UI設定データ ---
    private bodyControls: any = {
        tilt: '首の傾き',
        rArm: '右腕上げ',
        lArm: '左腕上げ',
        rLeg: '右足',
        lLeg: '左足',
        lean: '前後のしなり',
        sideTilt: '左右の傾き'
    };

    private sceneControls: any = {
        rot: '全体回転',
        zoom: 'ズーム'
    };

    private ranges: any = {
        tilt: { min: -45, max: 45 },
        rArm: { min: -30, max: 110 },
        lArm: { min: -30, max: 110 },
        rLeg: { min: -40, max: 40 },
        lLeg: { min: -40, max: 40 },
        lean: { min: -30, max: 30 },
        sideTilt: { min: -20, max: 20 },
        rot: { min: 0, max: 360 },
        zoom: { min: 1, max: 10, step: 0.1 },
        danceSpeed: { min: 0.5, max: 5.0, step: 0.1 }
    };

    // --- Three.js インスタンス (リアクティブ非対象) ---
    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private model: THREE.Group | null = null;
    private initialRotations: Map<string, THREE.Euler> = new Map();

    // --- Computed ---
    get currentURL(): string {
        const p = this.params;
        return `?tilt=${p.tilt}&rArm=${p.rArm}&lArm=${p.lArm}&rLeg=${p.rLeg}&lLeg=${p.lLeg}&lean=${p.lean}&sideTilt=${p.sideTilt}&rot=${p.rot}&zoom=${p.zoom}`;
    }

    get danceBtnStyle() {
        return {
            background: this.isDancing ? '#44ff44' : '#ff4444',
            color: 'white',
            fontWeight: 'bold'
        };
    }

    // --- Lifecycle ---
    mounted() {
        this.$nextTick(() => {
            this.initThree();
            window.addEventListener('resize', this.onWindowResize);
        });
    }

    // --- Methods ---
    private initThree() {
        // $el ではなく、ref で指定した div を取得する
        const container = this.$refs.threeContainer as HTMLElement;
        if (!container) return;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x111111); // これで「黒」になるはず

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(container.clientWidth, container.clientHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);

        // 指定した div の中に Canvas を入れる
        container.appendChild(this.renderer.domElement);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.9); // 環境光
        this.scene.add(ambientLight);
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6); // 太陽光
        directionalLight.position.set(1, 2, 3);
        this.scene.add(directionalLight);

        this.camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
        this.camera.position.set(0, 1.2, 3);
        this.camera.lookAt(0, 0.3, 0); // 視点を胸のあたりに固定

        this.axesHelper = new THREE.AxesHelper(5);
        this.scene.add(this.axesHelper);

        // 4. モデル読み込み
        const loader = new GLTFLoader();
        loader.load('https://6zr.github.io/medium/resources/glb/character_base.glb', (gltf) => {
            this.model = gltf.scene;

            this.scene.add(this.model);
            console.log("モデル読み込み成功！", this.model);

            // モデルを原点(0,0,0)に強制配置
            this.model.position.set(0, 0, 0);

            this.model.traverse((obj: any) => {
                // ここで空だった initialRotations に名前とポーズをセット！
                this.initialRotations.set(obj.name, obj.rotation.clone());
            });

            // ボーン初期化...
            this.animate();

        }, undefined, (error) => {
            console.error("モデル読み込みエラー:", error);
        });
    }

    public toggleDance() {
        this.isDancing = !this.isDancing;
        if (this.isDancing) this.lastPoseChangeTime = 0;
    }

    public randomize() {
        Object.keys(this.ranges).forEach((key: string) => {
            if (key === 'danceSpeed') return;
            if (this.params.frozen && this.params.frozen[key]) return; // 固定されている場合はスキップ
            const r = this.ranges[key];
            const val = Math.random() * (r.max - r.min) + r.min;
            this.params[key] = key === 'zoom' ? parseFloat(val.toFixed(1)) : Math.round(val);
        });
    }

    private initAudio() {
        if (this.audioCtx) return;
        
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        this.masterFilter = this.audioCtx.createBiquadFilter();
        this.masterFilter.type = 'lowpass';
        this.masterFilter.Q.value = 1;
        
        this.masterGain = this.audioCtx.createGain();
        this.masterGain.gain.value = 0.5; // 全体の音量
        
        this.masterFilter.connect(this.masterGain);
        this.masterGain.connect(this.audioCtx.destination);
        
        this.nextNoteTime = this.audioCtx.currentTime;
    }

    private handleAudioToggle() {
        if (this.isAudioEnabled) {
            this.initAudio();
            if (this.audioCtx?.state === 'suspended') {
                this.audioCtx.resume();
            }
        }
    }

    private updateWaveform() {
        // 現在は playNote 内で params.waveform を参照しているため個別更新は不要
    }

    private getFreq(index: number): number {
        const i = Math.max(0, Math.min(this.scale.length - 1, Math.floor(index)));
        return this.scale[i];
    }

    private playNote(freq: number, time: number, type: 'melody' | 'drum' = 'melody') {
        if (!this.audioCtx || !this.masterFilter) return;

        const osc = this.audioCtx.createOscillator();
        const g = this.audioCtx.createGain();
        
        osc.connect(g);
        g.connect(this.masterFilter);

        if (type === 'melody') {
            osc.type = this.params.waveform;
            osc.frequency.setValueAtTime(freq, time);
            
            // エンベロープ (音の立ち上がりと消え方)
            // 腕の上げ具合で「余韻」の長さを変える
            const release = 0.1 + ((this.current.rArm + this.current.lArm + 60) / 200) * 0.5;
            
            g.gain.setValueAtTime(0, time);
            g.gain.linearRampToValueAtTime(0.2, time + 0.02);
            g.gain.exponentialRampToValueAtTime(0.001, time + release);
            
            osc.start(time);
            osc.stop(time + release + 0.1);
        } else {
            // ドラム (バスドラム的な低い音)
            osc.type = 'sine';
            osc.frequency.setValueAtTime(150, time);
            osc.frequency.exponentialRampToValueAtTime(40, time + 0.1);
            
            g.gain.setValueAtTime(0, time);
            g.gain.linearRampToValueAtTime(0.6, time + 0.01);
            g.gain.exponentialRampToValueAtTime(0.001, time + 0.2);
            
            osc.start(time);
            osc.stop(time + 0.3);
        }
    }

    private scheduler() {
        if (!this.audioCtx || !this.isAudioEnabled || !this.isDancing) return;

        // 1/8拍子ごとにスケジュール (danceSpeedが1ポーズ/2秒なら、8分音符は0.25秒)
        const secondsPerBeat = (this.params.danceSpeed / 8); 
        
        while (this.nextNoteTime < this.audioCtx.currentTime + 0.1) {
            // 8拍（1サイクル）ごとにポーズをランダム化 (音と同期)
            if (this.beatCount % 8 === 0) {
                this.randomize();
            }

            const c = this.current;
            
            // 1. アルペジオ (伴奏: 1/8拍子)
            const baseIdx = (c.tilt + 45) / 10 + 5;
            const octaveShift = Math.floor(c.lean / 20) * 2;
            
            // 拍数に応じて和音の音を分散させる (0, 2, 4, 7 ...)
            const chordPattern = [0, 2, 4, 7, 5, 2, 0, -2];
            const noteIdx = baseIdx + octaveShift + chordPattern[this.beatCount % 8];
            
            this.playNote(this.getFreq(noteIdx), this.nextNoteTime, 'melody');

            // 1b. 主旋律 (メロディ: 1/2拍子 = 4ステップごと)
            // アルペジオより1オクターブ高く、ゆったり鳴らす
            if (this.beatCount % 4 === 0) {
                // 首の傾き(tilt)を主旋律の跳躍幅にする
                const melodyJump = Math.floor(c.tilt / 15);
                const melodyIdx = baseIdx + octaveShift + 5 + melodyJump; // +5でオクターブ上げ
                this.playNote(this.getFreq(melodyIdx), this.nextNoteTime, 'melody');
            }

            // 2. リズム (4拍に1回ドラム)
            if (this.beatCount % 8 === 0) {
                this.playNote(60, this.nextNoteTime, 'drum');
            }

            // 3. フィルター更新 (マスターにかける)
            const armSum = (c.rArm + c.lArm + 60) / 220; 
            const cutoff = 600 + (armSum * 4000);
            this.masterFilter?.frequency.setTargetAtTime(cutoff, this.nextNoteTime, 0.1);

            this.nextNoteTime += secondsPerBeat;
            this.beatCount++;
        }
    }

    private animate() {
        // console.log("Looping...");
        requestAnimationFrame(this.animate);

        this.updateLerp();
        this.updateBones();
        this.scheduler();

        if (this.axesHelper) {
            this.axesHelper.visible = this.params.showGrid;
        }

        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        }
    }

    private updateLerp() {
        const lerpSpeed = 0.1;
        // currentの各値をparamsにじわじわ近づける
        Object.keys(this.current).forEach((key: string) => {
            this.current[key] += (this.params[key] - this.current[key]) * lerpSpeed;
        });
    }

    private updateBones() {
        if (!this.model) return;

        const toRad = (d: number) => d * (Math.PI / 180);
        const c = this.current;

        this.model.traverse((obj: any) => {
            if (obj.isBone && this.initialRotations.has(obj.name)) {
                const init = this.initialRotations.get(obj.name)!;
                obj.rotation.copy(init);

                if (obj.name === 'ボーン003') obj.rotation.z += toRad(c.tilt);
                if (obj.name === 'ボーン006R') obj.rotation.z += toRad(c.rArm);
                if (obj.name === 'ボーン006L') obj.rotation.z += toRad(-c.lArm);
                if (obj.name === 'ボーン009R') obj.rotation.x += toRad(c.rLeg);
                if (obj.name === 'ボーン009L') obj.rotation.x += toRad(c.lLeg);
                if (obj.name === 'ボーン002') obj.rotation.x += toRad(c.lean * 0.6);
                if (obj.name === 'ボーン001') {
                    obj.rotation.x += toRad(c.lean * 0.4);
                    obj.rotation.z += toRad(c.sideTilt);
                }
            }
        });

        // モデル全体の変形・位置補正
        this.model.rotation.y = toRad(c.rot);
        this.model.rotation.z = toRad(c.sideTilt);
        this.model.position.y = Math.abs(c.sideTilt) * 0.02; // 足の埋まり防止
        this.model.position.x = toRad(c.sideTilt) * -0.5;    // 重心移動
        this.camera.position.z = c.zoom;
    }

    private onWindowResize() {
        if (!this.camera || !this.renderer) return;
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
}
